import {
  getLeoUuid,
  LeoUUID,
  getBoolean,
  getString,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetRespondentFilterOptionsRPC implements
  LeoRPC<GetRespondentFilterOptionsRPC.Request, GetRespondentFilterOptionsRPC.Response, GetRespondentFilterOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetRespondentFilterOptionsRPC.Request
  ): Promise<LeoRPCResult<GetRespondentFilterOptionsRPC.Response, GetRespondentFilterOptionsRPC.Errors.Errors>>;
}

export namespace GetRespondentFilterOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    isLive: boolean;
    filter: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetRespondentFilterOptionsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly isLive: boolean;
    readonly filter: string;
  
  
    constructor(
    projectId: LeoUUID, 
    isLive: boolean, 
    filter: string
    ) {
      this.projectId = projectId;
      this.isLive = isLive;
      Request.validateFilter(filter);
      this.filter = filter;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const isLive = getBoolean(dto, "isLive", InvalidRequestIsLiveError);
      const filter = getString(dto, "filter", InvalidRequestFilterError);
      return new Request(
        projectId,
        isLive,
        filter
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          isLive: this.isLive,
          filter: this.filter
      };
      return dto;
    }
    private static validateFilter = (filter: string): void => {
      if(filter.trim().length === 0) {
        throw new InvalidRequestFilterNonEmptyError(
          `Attribute filter cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetRespondentFilterOptionsRPC(projectId='${this.projectId}',isLive='${this.isLive}',filter='${this.filter}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    filterOptions: string[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetRespondentFilterOptionsResponse: boolean = true;
  
    readonly filterOptions: string[];
  
  
    constructor(
    filterOptions: string[]
    ) {
      this.filterOptions = filterOptions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const filterOptions = getList<string, string>(
        dto,
        "filterOptions",
        (e: string) => e,
        InvalidResponseFilterOptionsError
      );
      return new Response(
        filterOptions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          filterOptions: this.filterOptions
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetRespondentFilterOptions(filterOptions='${this.filterOptions}')`;
  };
  

  export enum RPCError {
    InvalidFilter = "INVALID_FILTER",
    RespondentUpdateInProgress = "RESPONDENT_UPDATE_IN_PROGRESS",
    InvalidProjectId = "INVALID_PROJECT_ID",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    TooManyUniqueValues = "TOO_MANY_UNIQUE_VALUES"
  }

  export namespace Errors {
    
    export interface InvalidFilterDTO {
      code: string;
    }
    
    export class InvalidFilter extends LeoRPCError<RPCError> {
      readonly isInvalidFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidFilter;
        super(code);
      }
    
    
      toDTO(): InvalidFilterDTO {
        const dto: InvalidFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidFilter.prototype.toString = function toString(): string {
      return `GetRespondentFilterOptionsRPC.INVALID_FILTER()`;
    };
    
    
    export interface RespondentUpdateInProgressDTO {
      code: string;
    }
    
    export class RespondentUpdateInProgress extends LeoRPCError<RPCError> {
      readonly isRespondentUpdateInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.RespondentUpdateInProgress;
        super(code);
      }
    
    
      toDTO(): RespondentUpdateInProgressDTO {
        const dto: RespondentUpdateInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    RespondentUpdateInProgress.prototype.toString = function toString(): string {
      return `GetRespondentFilterOptionsRPC.RESPONDENT_UPDATE_IN_PROGRESS()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetRespondentFilterOptionsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `GetRespondentFilterOptionsRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface TooManyUniqueValuesDTO {
      code: string;
    }
    
    export class TooManyUniqueValues extends LeoRPCError<RPCError> {
      readonly isTooManyUniqueValues: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.TooManyUniqueValues;
        super(code);
      }
    
    
      toDTO(): TooManyUniqueValuesDTO {
        const dto: TooManyUniqueValuesDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    TooManyUniqueValues.prototype.toString = function toString(): string {
      return `GetRespondentFilterOptionsRPC.TOO_MANY_UNIQUE_VALUES()`;
    };
    

    export type Errors = InvalidFilter | RespondentUpdateInProgress | InvalidProjectId | DeletedColumnFilter | TooManyUniqueValues;
    export type ErrorsDTO = InvalidFilterDTO | RespondentUpdateInProgressDTO | InvalidProjectIdDTO | DeletedColumnFilterDTO | TooManyUniqueValuesDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFilter:
          return new InvalidFilter();
        case RPCError.RespondentUpdateInProgress:
          return new RespondentUpdateInProgress();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.TooManyUniqueValues:
          return new TooManyUniqueValues();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetRespondentFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestIsLiveError extends GetRespondentFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestIsLiveError";
  }
}

export class InvalidRequestFilterError extends GetRespondentFilterOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilterError";
  }
}

export class InvalidRequestFilterNonEmptyError extends InvalidRequestFilterError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilterNonEmptyError";
  }
}

export class InvalidResponseFilterOptionsError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFilterOptionsError";
  }
}

export class InvalidResponseInvalidFilterError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFilterError";
  }
}

export class InvalidResponseRespondentUpdateInProgressError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseRespondentUpdateInProgressError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseTooManyUniqueValuesError extends GetRespondentFilterOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTooManyUniqueValuesError";
  }
}

